import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (!email.trim()) {
      setStatus('error');
      setMessage('Email is required');
      return;
    }

    setStatus('loading');

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const apiKey = import.meta.env.VITE_API_KEY;
      const response = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey && { 'x-api-key': apiKey }),
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        // Don't reveal whether the email exists in the system.
        // Show the same success message regardless of the response.
        console.warn('Forgot password request failed:', response.status);
      }

      setStatus('success');
      setMessage('If an account exists with that email, we\'ll send you a link to reset your password. Please check your inbox.');
    } catch (error: any) {
      // Only show an error for network failures, not for user-not-found
      setStatus('error');
      setMessage('Unable to connect to the server. Please check your connection and try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#3b4d66',
        paddingTop: '15vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '48px 40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
        }}
      >
        <img
          src="/logo-dv-full-text.png"
          alt="DV Logo"
          style={{
            height: '40px',
            marginBottom: '32px',
          }}
        />

        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          Reset your password
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the email associated with your account and we will send you a reset link.
        </Typography>

        {status === 'error' && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        {status === 'success' && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
            disabled={status === 'loading' || status === 'success'}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={status === 'loading' || status === 'success'}
            sx={{ mt: 1 }}
          >
            {status === 'loading' ? 'Sending link...' : 'Send reset link'}
          </Button>
        </Box>

        <Button
          variant="text"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Back to login
        </Button>
      </Box>
    </Box>
  );
};
